import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { PageHeader, Panel } from "../components/common";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { DeployWizard } from "../components/hub/DeployWizard";
import { KeysPanel, WebhooksPanel } from "../components/hub/KeysWebhooks";
import { CONNECTORS } from "../components/hub/catalog";
import { CodeBlock } from "../components/hub/CodeBlock";

const API = process.env.REACT_APP_BACKEND_URL;
const DOCS = [
  ["GET", "/api/v1/ping", "any", "Check that a key works"],
  ["POST", "/api/v1/transactions", "transactions:write", "{id?, customer_email, customer_name?, amount, product_name?, currency?}"],
  ["POST", "/api/v1/events", "events:write", "{transaction_id, customer_email, type, description, metadata?, timestamp?}"],
  ["POST", "/api/v1/events/batch", "events:write", "Array of events, max 500"],
  ["GET", "/api/v1/disputes?status=", "disputes:read", "Disputes for reconciliation"],
];

const Connectors = () => (
  <div className="grid md:grid-cols-2 gap-6" data-testid="connectors">
    {CONNECTORS.map((c) => (
      <Panel key={c.id} title={`${c.name} · template connector`} testid={`connector-${c.id}`}>
        <p className="font-mono text-[10px] text-[#FFB800] mb-2">TEMPLATE · BUILT ON SIGNED WEBHOOKS + OPEN API · NATIVE OAUTH COMING</p>
        <p className="text-sm text-white/75">{c.purpose}</p>
        <p className="label-xs mt-4">Subscribe to</p><p className="font-mono text-[11px] text-brand mt-1">{c.events.join(" · ")}</p>
        <table className="w-full mt-4 text-xs"><thead><tr className="text-white/40 text-left"><th className="py-1 font-normal">AEGIS field</th><th className="font-normal">{c.name} field</th></tr></thead>
          <tbody>{c.map.map(([a, b]) => <tr key={a} className="border-t border-white/5"><td className="py-1.5 font-mono text-white/70">{a}</td><td className="text-white/80">{b}</td></tr>)}</tbody></table>
        <ol className="mt-4 space-y-1 text-xs text-white/65 list-decimal pl-4">{c.steps.map((s) => <li key={s}>{s}</li>)}</ol>
      </Panel>
    ))}
  </div>
);

const Docs = () => (
  <div className="space-y-6" data-testid="api-docs">
    <Panel title="Open API · authenticate with the X-AEGIS-Key header">
      <table className="w-full text-xs"><tbody>{DOCS.map(([m, p, s, d]) => (
        <tr key={p} className="border-b border-white/5"><td className="py-2 font-mono text-brand w-14">{m}</td><td className="font-mono text-white/80">{p}</td><td className="font-mono text-accent2">{s}</td><td className="text-white/55">{d}</td></tr>))}</tbody></table>
      <p className="text-xs text-white/45 mt-3">Evidence types: payment_auth, device_fingerprint, login, content_access, file_download, delivery, email_confirmation, submission, consent. Every event is SHA-256 hashed, HMAC-signed and hash-chained per transaction.</p>
    </Panel>
    <CodeBlock title="Base URL" code={`${API}/api/v1`} testid="docs-base-url" />
  </div>
);

export default function IntegrationHub() {
  const [health, setHealth] = useState(null);
  const [keys, setKeys] = useState([]);
  const { isOwner } = useAuth();
  const load = useCallback(() => Promise.all([api.get("/hub/health"), isOwner ? api.get("/hub/keys") : Promise.resolve({ data: [] })]).then(([h, k]) => { setHealth(h.data); setKeys(k.data); }).catch(() => {}), [isOwner]);
  useEffect(() => { load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, [load]);
  const healthy = health?.platforms?.filter((p) => p.status === "healthy").length ?? 0;
  return (
    <div data-testid="integration-hub">
      <PageHeader eyebrow="// deploy in minutes · tos, checkout, lms, erp, crm" title="Integration Hub">
        <span className="font-mono text-xs text-white/55" data-testid="hub-summary">{healthy} healthy · {keys.filter((k) => k.status === "active").length} keys · {health?.endpoints ?? 0} webhooks</span>
      </PageHeader>
      <Tabs defaultValue="deploy">
        <TabsList className="bg-transparent border border-white/10 rounded-none mb-6 flex-wrap h-auto">
          {[["deploy", "Deploy wizard"], ["keys", "API keys"], ["webhooks", "Webhooks"], ["connectors", "ERP / CRM"], ["docs", "API docs"]].map(([v, l]) => (
            <TabsTrigger key={v} value={v} className="rounded-none font-mono text-xs data-[state=active]:bg-white data-[state=active]:text-black" data-testid={`hub-tab-${v}`}>{l}</TabsTrigger>))}
        </TabsList>
        <TabsContent value="deploy"><DeployWizard health={health} onChange={load} /></TabsContent>
        <TabsContent value="keys"><KeysPanel keys={keys} onChange={load} /></TabsContent>
        <TabsContent value="webhooks"><WebhooksPanel onChange={load} /></TabsContent>
        <TabsContent value="connectors"><Connectors /></TabsContent>
        <TabsContent value="docs"><Docs /></TabsContent>
      </Tabs>
    </div>
  );
}
